/**
 * QRLookupDialog
 * ==============
 * Scans a QR code printed on an Urban Utility label and fires `onFound` with
 * the variant ID so the caller can open the variant-details popup.
 *
 * QR payload format (same as InventoryCheckDialog):
 *   URBAN|<variantId>|<artId>|<sku>|<size>|<condition>
 *
 * Usage:
 *   <QRLookupDialog open={open} onClose={() => setOpen(false)} onFound={(id) => openVariant(id)} />
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, QrCodeScanner as QrCodeScannerIcon } from '@mui/icons-material';

// ── Audio helpers ─────────────────────────────────────────────────────────────

function beep(frequency: number, durationMs: number, volume = 0.4) {
  try {
    const ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = frequency;
    osc.type = 'sine';
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + durationMs / 1000);
  } catch {
    // Audio not available — ignore
  }
}

const successBeep = () => beep(880, 120);

// ── QR payload parser ─────────────────────────────────────────────────────────

function parseQRPayload(raw: string): { variantId: number } | null {
  const parts = raw.split('|');
  if (parts[0] !== 'URBAN' || parts.length < 2) return null;
  const id = Number(parts[1]);
  if (!Number.isFinite(id) || id <= 0) return null;
  return { variantId: id };
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called once when a valid QR is decoded. Dialog closes automatically. */
  onFound: (variantId: number) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function QRLookupDialog({ open, onClose, onFound }: Props): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const foundRef = useRef(false);

  const [cameraError, setCameraError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // ── Camera lifecycle ──────────────────────────────────────────────────────

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setCameraError(`No se pudo acceder a la cámara: ${msg}`);
    }
  }, []);

  // ── Scan loop ─────────────────────────────────────────────────────────────

  const scan = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const qr = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });

    if (qr) {
      const parsed = parseQRPayload(qr.data);
      if (parsed && !foundRef.current) {
        foundRef.current = true;
        successBeep();
        stopCamera();
        onFound(parsed.variantId);
        onClose();
        return;
      }
      if (!parsed) {
        setHint('QR no reconocido. Asegúrate de escanear una etiqueta Urban.');
      }
    }

    // ~15 fps
    setTimeout(() => {
      rafRef.current = requestAnimationFrame(scan);
    }, 66);
  }, [stopCamera, onFound, onClose]);

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      foundRef.current = false;
      setCameraError(null);
      setHint(null);
      startCamera()
        .then(() => {
          rafRef.current = requestAnimationFrame(scan);
        })
        .catch(() => {});
    }
    return () => {
      stopCamera();
    };
  }, [open, startCamera, scan, stopCamera]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={() => {
        stopCamera();
        onClose();
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <QrCodeScannerIcon sx={{ color: '#111827' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Escanear etiqueta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Apunta la cámara al QR de la etiqueta
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => {
            stopCamera();
            onClose();
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 3 }}>
        {cameraError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {cameraError}
          </Alert>
        ) : (
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#000',
              aspectRatio: '4/3',
              mt: 1,
            }}
          >
            <video
              ref={videoRef}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              muted
              playsInline
            />
            {/* Crosshair overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  width: 180,
                  height: 180,
                  border: '2px solid rgba(255,255,255,0.7)',
                  borderRadius: 1,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.30)',
                }}
              />
            </Box>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>
        )}

        {hint && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {hint}
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default QRLookupDialog;
